import { BRAND } from "@/data/content";
import { resolveAssetPath } from "@/lib/assets";

const NAVY = [11, 37, 69];
const YELLOW = [245, 183, 0];
const GREY = [90, 98, 112];
const LIGHT = [245, 247, 250];

const rs = (n) => (n == null ? "Price on request" : `Rs. ${Number(n).toLocaleString("en-IN")}`);
const clean = (s) => String(s ?? "").replace(/₹/g, "Rs. ").replace(/[–—]/g, "-").replace(/[’‘]/g, "'").replace(/[“”]/g, '"').replace(/·/g, "|").replace(/…/g, "...");

// Load an image URL into a PNG/JPEG data URL (via canvas) so jsPDF can embed it.
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

const loadImage = (src, type = "image/jpeg") =>
  new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const c = document.createElement("canvas");
      c.width = img.naturalWidth;
      c.height = img.naturalHeight;
      const ctx = c.getContext("2d");
      if (type === "image/jpeg") { ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, c.width, c.height); }
      ctx.drawImage(img, 0, 0);
      resolve({ data: c.toDataURL(type, 0.85), w: c.width, h: c.height });
    };
    img.onerror = () => resolve(null);
    img.src = resolveAssetPath(src);
  });

const dayDate = (start, offset) => {
  if (!start) return "";
  const d = new Date(start);
  if (Number.isNaN(d.getTime())) return "";
  d.setDate(d.getDate() + offset);
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

export async function buildItineraryPdf({ tour, departure, depCity, itinerary, inclusionsText, exclusionsText, prepText, docs, cancelTiers, flightRoute }) {
  const [{ jsPDF }, { default: autoTable }] = await Promise.all([import("jspdf"), import("jspdf-autotable")]);
  const [logo, banner] = await Promise.all([loadImage(BRAND.logo, "image/png"), loadImage(tour.image)]);

  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const M = 14;
  let y = 0;

  const header = () => {
    doc.setFillColor(...NAVY);
    doc.rect(0, 0, W, 20, "F");
    if (logo) doc.addImage(logo.data, "PNG", M, 3, (14 * logo.w) / logo.h, 14);
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text(BRAND.shortName, W - M, 9, { align: "right" });
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.text(`${BRAND.phone}  |  ${BRAND.email}`, W - M, 14.5, { align: "right" });
  };
  const ensure = (need) => {
    if (y + need > H - 18) { doc.addPage(); header(); y = 28; }
  };
  const heading = (text, need = 14) => {
    ensure(need);
    doc.setFillColor(...YELLOW);
    doc.rect(M, y, 2, 6, "F");
    doc.setTextColor(...NAVY);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12.5);
    doc.text(text, M + 5, y + 4.8);
    y += 10;
  };
  const para = (text, { size = 9.5, color = GREY, indent = 0, bold = false } = {}) => {
    const style = () => {
      doc.setFont("helvetica", bold ? "bold" : "normal");
      doc.setFontSize(size);
      doc.setTextColor(...color);
    };
    style();
    const lines = doc.splitTextToSize(clean(text), W - 2 * M - indent);
    lines.forEach((ln) => { ensure(5); style(); doc.text(ln, M + indent, y); y += size * 0.45; });
  };
  const bullets = (items) => {
    items.forEach((t) => {
      ensure(6);
      doc.setFillColor(...YELLOW);
      doc.circle(M + 1.5, y - 1.2, 0.9, "F");
      para(t, { indent: 5 });
      y += 1.2;
    });
    y += 2;
  };

  // ── Cover
  header();
  y = 28;
  if (banner) {
    const bh = Math.min(78, ((W - 2 * M) * banner.h) / banner.w);
    doc.addImage(banner.data, "JPEG", M, y, W - 2 * M, bh, undefined, "MEDIUM");
    y += bh + 8;
  }
  doc.setTextColor(...NAVY);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(22);
  doc.splitTextToSize(clean(tour.title), W - 2 * M).forEach((ln) => { doc.text(ln, M, y); y += 8.5; });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(...GREY);
  const facts = [
    tour.code && `Tour code: ${tour.code}`,
    `${tour.days} Days / ${tour.nights ?? tour.days - 1} Nights`,
    tour.countriesCount ? plural(tour.countriesCount, "Country", "Countries") : tour.statesCount ? plural(tour.statesCount, "State", "States") : null,
    tour.citiesCount && plural(tour.citiesCount, "City", "Cities"),
    tour.packageType,
  ].filter(Boolean).join("   |   ");
  doc.text(clean(facts), M, y);
  y += 7;

  const firstDate = departure?.date || tour.dates?.[0]?.date;
  const price = departure?.price ?? tour.startingPrice;
  autoTable(doc, {
    startY: y,
    theme: "plain",
    margin: { left: M, right: M },
    styles: { fontSize: 9.5, cellPadding: 3, textColor: NAVY },
    columnStyles: { 0: { fontStyle: "bold", cellWidth: 52, fillColor: LIGHT }, 1: { fillColor: [255, 255, 255] } },
    body: [
      [departure ? "Price for your departure" : "Starting price (per person)", price == null ? "Price on request" : `${rs(price)}*  per person (twin sharing)`],
      ...(tour.joiningLeavingPrice != null ? [["Joining / Leaving price", `${rs(tour.joiningLeavingPrice)}*`]] : []),
      ...(tour.emiPerMonth != null ? [["EMI starts at", `${rs(tour.emiPerMonth)} / month`]] : []),
      ["Departure city", clean(tour.departureCity || depCity || "On request")],
      [departure ? "Your departure date" : "Next departure", firstDate ? `${clean(firstDate)}${tour.days ? `  to  ${dayDate(firstDate, tour.days - 1)}` : ""}` : "Flexible - on request"],
      ["Route", clean(tour.route.map((r) => `${r.state}: ${r.stops.join(", ")}`).join("  |  "))],
      ["Inclusions", ["Hotel", "Meals", "Flight", "Sightseeing", "Transport"].filter((k) => tour.inclusions?.[k.toLowerCase()]).map((k) => (tour.inclusions[k.toLowerCase()] === "optional" ? `${k} (optional)` : k)).join(", ")],
    ],
    didParseCell: (d) => { d.cell.styles.lineColor = [228, 231, 236]; d.cell.styles.lineWidth = 0.2; },
  });
  y = doc.lastAutoTable.finalY + 4;
  para("*Prices may vary as per your travel date and departure city. 5% GST applicable.", { size: 8 });
  y += 4;

  if (tour.highlights?.length) {
    heading("Tour Highlights");
    bullets(tour.highlights);
  }

  // ── Day-wise itinerary
  heading("Day-wise Itinerary");
  itinerary.forEach((d, i) => {
    const date = dayDate(firstDate, (d.day || i + 1) - 1);
    ensure(26);
    doc.setFillColor(...LIGHT);
    doc.roundedRect(M, y - 4.5, W - 2 * M, 7.5, 1.5, 1.5, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(...NAVY);
    doc.text(`Day ${d.day || i + 1}${date ? `  |  ${date}` : ""}`, M + 3, y);
    doc.setTextColor(...GREY);
    doc.setFont("helvetica", "normal");
    if (d.state) doc.text(clean(d.state), W - M - 3, y, { align: "right" });
    y += 7;
    para(d.title, { size: 10.5, color: NAVY, bold: true });
    y += 0.5;
    para(d.desc);
    y += 4;
  });

  heading("Tour Inclusions");
  bullets(inclusionsText);
  heading("Tour Exclusions");
  bullets(exclusionsText);

  if (tour.dates?.length) {
    heading("Departure Dates", 40);
    autoTable(doc, {
      startY: y,
      margin: { left: M, right: M, top: 28 },
      head: [["Departure date", "Price per person", "Status"]],
      body: tour.dates.slice(0, 24).map((d) => [clean(d.date) + (d.date === departure?.date ? "  (selected)" : ""), rs(d.price ?? tour.startingPrice), clean(d.tag || (d.seats ? `${d.seats} seats left` : "Available"))]),
      styles: { fontSize: 9, cellPadding: 2.5 },
      headStyles: { fillColor: NAVY, textColor: 255 },
      alternateRowStyles: { fillColor: LIGHT },
      didDrawPage: () => header(),
    });
    y = doc.lastAutoTable.finalY + 8;
  }

  heading("Need to Know");
  para(`Transport: Air travel ${flightRoute}. ${tour.travelInfo?.coach || "Coach travel by A/C vehicle - type depends on group size."}`);
  y += 3;
  para("Documents required for travel", { color: NAVY, bold: true });
  y += 1;
  bullets(docs);
  para("Advance preparation", { color: NAVY, bold: true });
  y += 1;
  bullets(prepText);

  if (cancelTiers.length) {
    heading("Cancellation Policy", 40);
    autoTable(doc, {
      startY: y,
      margin: { left: M, right: M, top: 28 },
      head: [["Period before departure", "Deduction", "Amount per person"]],
      body: cancelTiers.map((t) => [clean(t.window), `${t.pct}%`, rs(t.amount)]),
      styles: { fontSize: 9, cellPadding: 2.5 },
      headStyles: { fillColor: NAVY, textColor: 255 },
      alternateRowStyles: { fillColor: LIGHT },
      didDrawPage: () => header(),
    });
    y = doc.lastAutoTable.finalY + 6;
  }
  para("Payment by cash / cheque / demand draft / debit or credit card. Card payments carry a 0.50% convenience charge. Prices are subject to availability and may change without notice.", { size: 8.5 });

  // ── Footer on every page
  const pages = doc.getNumberOfPages();
  for (let p = 1; p <= pages; p++) {
    doc.setPage(p);
    doc.setDrawColor(...YELLOW);
    doc.setLineWidth(0.6);
    doc.line(M, H - 13, W - M, H - 13);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(...GREY);
    doc.text(clean(`${BRAND.name}  |  ${BRAND.phone}  |  ${BRAND.email}`), M, H - 8);
    doc.text(`Page ${p} of ${pages}`, W - M, H - 8, { align: "right" });
    doc.text(clean(`Head Office: ${BRAND.address}`), M, H - 4.5);
  }

  const dd = departure?.date ? new Date(departure.date) : null;
  const dateTag = dd && !Number.isNaN(dd.getTime()) ? `-${dd.getFullYear()}-${String(dd.getMonth() + 1).padStart(2, "0")}-${String(dd.getDate()).padStart(2, "0")}` : "";
  const file = `${tour.title.replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "")}-${tour.code || "itinerary"}${dateTag}-Itinerary.pdf`;
  return { doc, file };
}

export async function downloadItineraryPdf(opts) {
  const { doc, file } = await buildItineraryPdf(opts);
  doc.save(file);
  return file;
}

export async function itineraryPdfBase64(opts) {
  const { doc } = await buildItineraryPdf(opts);
  return doc.output("datauristring").split(",")[1];
}
